import {ConsentString} from 'consent-string';
import {setLang} from '../translations';
import {cookieSaved, end, exit, profileError, profileSaved, show, submitClick} from '../services/measurement';
import {sendEvent} from '../services/customEvents';
import {setCookieToGlobalStorage} from '../services/globalCookie';
import {inProfile} from '../utils/scriptParams';
import {demoMode} from '../utils/urlParams';
import Dialog from '../components/Dialog.svelte';
import {logger} from './logger';
import {getVendors, loadVendors, setVendorsToConsent} from '../vendors';
import {saveSettingsToBrowser, existsConsentCookie, hasConsentAlready} from './consent';
import {setPurposesToConsent} from '../purposes';
import {setFormDataLoggedInUser, getFormData} from './formData';
import {setProfileData, saveProfileError} from './profil';
import {currentHost} from '../utils/host';
import {
  CMP_ID,
  CMP_VERSION,
  DEFAULT_LANGUAGE,
  SEZNAM_VENDOR_ID,
  PAGE_INTRO,
  PAGE_PURPOSES,
  DIALOG_OPENED,
  DIALOG_CLOSED,
  DIALOG_EXITED,
  DIALOG_FAILED,
  PURPOSE_ID_CONTENT,
  PURPOSE_ID_ADS,
  SZN_SETTING_PAGES
} from '../constants';

/** TO BE MOVED
 * Returns consent object with preset data.
 * @param {object} GVL
 * @param {object} screen The screen number is CMP and CMP Version specific, and is for logging proof of consent.
 * @return {ConsentString}
 */
const getEuconsentData = (GVL, screen) => {
  const consentData = new ConsentString();
  const formData = getFormData();

  consentData.setGlobalVendorList(GVL);
  consentData.setCreated(new Date());
  consentData.setCmpId(CMP_ID);
  consentData.setConsentLanguage(DEFAULT_LANGUAGE);
  consentData.setCmpVersion(CMP_VERSION);
  consentData.setConsentScreen(screen);

  setPurposesToConsent(consentData, formData.purposes);
  setVendorsToConsent(consentData, formData.vendors);

  return consentData;
};

/**
 * In case of failure dialog is closed. We do not want to block page with broken CMP.
 * @param {Dialog} dialog Svelte dialog instance.
 */
const onFailed = dialog => {
  dialog.$destroy();
  sendEvent(DIALOG_FAILED);
  logger(1000, 'Dialog was closed due to failure.');
  end();
};

/**
 * Make dialog closed
 */
const closeDialog = dialog => {
  dialog.$destroy();
  sendEvent(DIALOG_CLOSED);
  logger(1001, 'Dialog was closed after consent changed.');
  end();
};

/**
 * If user agreed close dialog and save data.
 * @param {Dialog} dialog Svelte dialog instance.
 */
const onAgreed = async (event, dialog, userLoggedIn, userType) => {
  try {
    const GVL = await getVendors();
    const {screen} = event.detail;

    const consentData = getEuconsentData(GVL, screen);
    const encodedCookie = consentData.getConsentString();

    const seznamAllowed = consentData.isVendorAllowed(SEZNAM_VENDOR_ID);
    const contentAllowed = consentData.isPurposeAllowed(PURPOSE_ID_CONTENT);
    const adsAllowed = consentData.isPurposeAllowed(PURPOSE_ID_ADS);

    submitClick(screen, seznamAllowed, contentAllowed, adsAllowed);

    // We don't want to save euconsent into browser on Profile page, partner.seznam.cz and o.seznam.cz!
    if (!inProfile() && !SZN_SETTING_PAGES.includes(currentHost)) {
      saveSettingsToBrowser(encodedCookie);

      if (existsConsentCookie()) {
        cookieSaved();
      }
    }
    // do ssp localstorage posilame vzdy
    setCookieToGlobalStorage(encodedCookie, userType);

    if (userLoggedIn) {
      const {purposes} = getFormData();

      try {
        await setProfileData(encodedCookie, consentData, purposes);
        profileSaved();
      } catch (error) {
        // Could not save data into Profil
        profileError();
        saveProfileError(encodedCookie);
      }
    }

    closeDialog(dialog);
  } catch (error) {
    onFailed(dialog);
  }
};

/**
 * If user closed dialog by exit icon.
 */
const onExit = (event, dialog) => {
  const {screen} = event.detail;

  dialog.$destroy();
  exit(screen);
  sendEvent(DIALOG_EXITED);
  logger(1002, 'Dialog was closed by exit icon.');
  end();
};

/**
 * Opens dialog/starts Svelte app.
 * @param {boolean} userLoggedIn
 * @param {boolean} hasPrevConsent
 * @param {string} lang Dialog language.
 */
const openDialog = (userLoggedIn, hasPrevConsent, lang, userType, consentPage = false) => {
  loadVendors();
  setLang(lang);
  show();

  const shouldOpenPurposes = inProfile() || SZN_SETTING_PAGES.includes(currentHost);
  const page = shouldOpenPurposes ? PAGE_PURPOSES : PAGE_INTRO;

  const dialog = new Dialog({
    intro: true,
    props: {
      consentPage,
      formData: getFormData(),
      hasPrevConsent,
      page,
      userLoggedIn,
      userType
    },
    target: document.body
  });

  sendEvent(DIALOG_OPENED);
  if (demoMode()) {
    logger(1003, 'Dialog was opened in demo - submiting is disabled.');
  } else {
    logger(1004, 'Dialog was opened.');
  }

  dialog.$on('agreed', event => onAgreed(event, dialog, userLoggedIn, userType));
  dialog.$on('failure', () => onFailed(dialog));
  dialog.$on('exit', event => onExit(event, dialog));
};

export const openUnloggedDialog = (hasPrevConsent, userType, lang = DEFAULT_LANGUAGE, consentPage = false) =>
  openDialog(false, hasPrevConsent, lang, userType, consentPage);
export const openLoggedDialog = (profileData, userType) => {
  setFormDataLoggedInUser(profileData);
  openDialog(true, hasConsentAlready(profileData), profileData.lang, userType);
};
